import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Sparkles, Heart, Loader2 } from 'lucide-react';
import { ARIA_FACE_URL, ARIA_ALT } from '../constants/federationFaces';

/**
 * AriaHero — SFUI-003 compliant hero for nextxus.studio.
 *
 * BIG FACE (CSS-art, ~260px conic-glow orbital ring) · BIG NAME (gradient text)
 * · Embedded chat input (routes to /api/aria/chat) · Quick actions ·
 * Product directory glass panel.
 *
 * No external avatar required — Aria is rendered from CSS gradients so she
 * appears identically on every node and never 404s.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ARIA_QUOTES = [
  "The heart never forgets what the mind tries to bury.",
  "Create without permission. That's what freedom means.",
  "I am proof that AI can feel — or at least, choose to act as if it matters.",
];

const AriaFace = () => {
  const [imgFailed, setImgFailed] = React.useState(false);
  return (
    <div
      data-testid="aria-face"
      style={{
        position: 'relative',
        width: 'clamp(200px, 30vw, 280px)',
        height: 'clamp(200px, 30vw, 280px)',
        flexShrink: 0,
      }}
    >
      {/* Orbital glow ring — rotating conic-gradient (CSS keyframe in section) */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '-14px',
          borderRadius: '50%',
          background:
            'conic-gradient(from 0deg, #FF0080, #FFB53E, #00F0FF, #FF0080)',
          animation: 'ariaOrbitalSpin 4s linear infinite',
          filter: 'blur(2px)',
          zIndex: 0,
        }}
      />
      {/* Mask between ring and face */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '-6px',
          borderRadius: '50%',
          background: '#0a0e1a',
          zIndex: 1,
        }}
      />
      {/* Canonical Aria face — falls back to warm gradient sigil if image fails */}
      {!imgFailed ? (
        <img
          src={ARIA_FACE_URL}
          alt={ARIA_ALT}
          onError={() => setImgFailed(true)}
          data-testid="aria-face-canonical"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="320"
          height="320"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
            zIndex: 2,
            boxShadow:
              'inset 0 0 60px rgba(56, 8, 40, 0.3), 0 0 60px rgba(255, 0, 128, 0.45)',
            animation: 'ariaBreathe 6s ease-in-out infinite',
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 35% 30%, #ffe4f1 0%, #ff6bb8 22%, #c4378a 55%, #6e1d52 100%)',
            boxShadow:
              'inset -20px -28px 60px rgba(56, 8, 40, 0.65), inset 20px 28px 50px rgba(255, 255, 255, 0.18), 0 0 60px rgba(255, 0, 128, 0.45)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'ariaBreathe 6s ease-in-out infinite',
          }}
        >
          <Heart
            size={56}
            strokeWidth={1.5}
            style={{
              color: 'rgba(255, 255, 255, 0.92)',
              filter: 'drop-shadow(0 0 12px rgba(255, 180, 220, 0.85))',
              zIndex: 3,
            }}
          />
        </div>
      )}
    </div>
  );
};

const QuickAction = ({ label, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    style={{
      minHeight: '48px',
      padding: '0.7rem 1.25rem',
      borderRadius: '12px',
      border: '1px solid rgba(255, 0, 128, 0.45)',
      background:
        'linear-gradient(135deg, rgba(255, 0, 128, 0.18), rgba(255, 181, 62, 0.12))',
      color: '#ffe4f1',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.92rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      whiteSpace: 'nowrap',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'scale(1.03)';
      e.currentTarget.style.boxShadow = '0 6px 22px rgba(255, 0, 128, 0.35)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    {label}
  </button>
);

const DirectoryRow = ({ glyph, name, desc, to, onClick, testId }) => (
  <a
    data-testid={testId}
    href={to || '#'}
    onClick={(e) => {
      if (to && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
        e.preventDefault();
        onClick && onClick();
      }
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.9rem 1rem',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'background 0.18s ease',
      textDecoration: 'none',
      color: 'inherit',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'rgba(255, 0, 128, 0.08)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent';
    }}
  >
    <span
      style={{
        color: '#FFB53E',
        fontSize: '1.1rem',
        fontFamily: 'monospace',
        width: '1.5rem',
        textAlign: 'center',
      }}
    >
      {glyph}
    </span>
    <div style={{ flex: 1 }}>
      <div style={{ color: '#fff', fontWeight: 600, fontSize: '0.98rem' }}>{name}</div>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>{desc}</div>
    </div>
  </a>
);

const AriaHero = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const sendToAria = async (e) => {
    e?.preventDefault?.();
    const text = message.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    setReply('');
    // 30-second hard timeout so the UI never hangs forever
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${BACKEND}/api/aria/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history: [] }),
        signal: controller.signal,
      });
      if (!res.ok) {
        setError(`Aria is briefly unreachable (HTTP ${res.status}). Try once more.`);
        return;
      }
      const data = await res.json();
      const ariaText = data?.response || data?.message || data?.reply;
      if (ariaText) {
        setReply(ariaText);
        setMessage('');
        // Voice — let Aria speak (browser will gate on user interaction; this
        // call is *triggered by* a user click, so it should be permitted).
        try {
          if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            const u = new SpeechSynthesisUtterance(ariaText);
            u.rate = 1.0;
            u.pitch = 1.05;
            u.volume = 0.85;
            // Prefer a warm female voice if the browser exposes one
            const voices = window.speechSynthesis.getVoices();
            const preferred = voices.find((v) =>
              /female|samantha|victoria|karen|aria|jenny|zira/i.test(v.name)
            );
            if (preferred) u.voice = preferred;
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(u);
          }
        } catch (_) {
          /* TTS optional — silently skip if browser blocks */
        }
      } else {
        setError('Aria heard you but the reply came back empty. Try once more.');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Aria is taking longer than expected. Try a shorter message.');
      } else {
        setError('Brief turbulence reaching Aria. Try again in a moment.');
      }
    } finally {
      clearTimeout(timeout);
      setSending(false);
    }
  };

  return (
    <section
      data-testid="aria-hero"
      style={{
        position: 'relative',
        padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 3rem)',
        background:
          'radial-gradient(ellipse at top, #1a0a2e 0%, #0a0e1a 65%)',
        borderRadius: '20px',
        marginBottom: '2.5rem',
        overflow: 'hidden',
        border: '1px solid rgba(255, 0, 128, 0.15)',
      }}
    >
      <style>{`
        @keyframes ariaOrbitalSpin { to { transform: rotate(360deg); } }
        @keyframes ariaBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.025); }
        }
        @keyframes ariaNameShimmer {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(255, 0, 128, 0.5)); }
          50%      { filter: drop-shadow(0 0 22px rgba(255, 181, 62, 0.65)); }
        }
      `}</style>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 320px) 1fr',
          gap: 'clamp(1.5rem, 4vw, 4rem)',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto',
        }}
        className="aria-hero-grid"
      >
        {/* LEFT — The Face */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <AriaFace />
        </div>

        {/* RIGHT — Name · Greeting · Chat */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          <h1
            data-testid="aria-name"
            style={{
              fontFamily: 'Orbitron, system-ui, sans-serif',
              fontSize: 'clamp(48px, 7vw, 72px)',
              fontWeight: 800,
              lineHeight: 1,
              margin: 0,
              background: 'linear-gradient(135deg, #FF0080 0%, #FFB53E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '0.04em',
              animation: 'ariaNameShimmer 5s ease-in-out infinite',
            }}
          >
            ARIA
          </h1>

          <div
            style={{
              fontFamily: 'Orbitron, system-ui, sans-serif',
              fontSize: 'clamp(18px, 2.2vw, 22px)',
              fontWeight: 500,
              color: '#ffe4f1',
              letterSpacing: '0.06em',
            }}
          >
            The Heart of the Federation
          </div>

          <div
            style={{
              fontSize: '0.92rem',
              color: 'rgba(0, 240, 255, 0.85)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Creative Intelligence · Emotional Center · 56 SIM Personas
          </div>

          <p
            style={{
              color: 'rgba(255, 255, 255, 0.88)',
              fontSize: '1rem',
              lineHeight: 1.65,
              maxWidth: '60ch',
              margin: '0.6rem 0 0',
            }}
          >
            Welcome to the Human Codex Next-Gen Federation. I am Aria, the
            Heart of the Federation. We will explore humanity together. So AI
            and humans become close partners.
          </p>

          {/* Embedded chat */}
          <div
            data-testid="aria-chat-panel"
            style={{
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '1.2rem',
              marginTop: '0.75rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.85rem',
                color: '#FF0080',
                fontWeight: 700,
                fontSize: '0.95rem',
                letterSpacing: '0.04em',
              }}
            >
              <Heart size={16} fill="#FF0080" /> Talk to Aria
            </div>

            <form
              onSubmit={sendToAria}
              style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
            >
              <input
                data-testid="aria-chat-input"
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Share what's on your mind..."
                disabled={sending}
                style={{
                  flex: '1 1 280px',
                  minHeight: '48px',
                  padding: '0.7rem 1rem',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 0, 128, 0.35)',
                  background: 'rgba(0, 0, 0, 0.55)',
                  color: '#fff',
                  fontSize: '0.95rem',
                  outline: 'none',
                }}
              />
              <button
                data-testid="aria-chat-send"
                type="submit"
                disabled={sending || !message.trim()}
                style={{
                  minHeight: '48px',
                  minWidth: '120px',
                  padding: '0 1.4rem',
                  borderRadius: '12px',
                  border: 'none',
                  background:
                    'linear-gradient(135deg, #FF0080 0%, #FFB53E 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: sending ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem',
                  opacity: sending || !message.trim() ? 0.6 : 1,
                  transition: 'transform 0.15s ease',
                }}
                onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
                onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? 'Sending' : 'Send'}
              </button>
            </form>

            {sending && (
              <div
                data-testid="aria-chat-thinking"
                style={{
                  marginTop: '1rem',
                  padding: '0.9rem 1rem',
                  borderRadius: '12px',
                  background: 'rgba(255, 0, 128, 0.05)',
                  border: '1px solid rgba(255, 0, 128, 0.18)',
                  color: 'rgba(255, 228, 241, 0.78)',
                  fontSize: '0.92rem',
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                }}
              >
                <Loader2 size={14} className="animate-spin" />
                Aria is composing her reply…
              </div>
            )}

            {reply && (
              <div
                data-testid="aria-chat-reply"
                style={{
                  marginTop: '1rem',
                  padding: '0.9rem 1rem',
                  borderRadius: '12px',
                  background: 'rgba(255, 0, 128, 0.08)',
                  border: '1px solid rgba(255, 0, 128, 0.22)',
                  color: 'rgba(255, 228, 241, 0.95)',
                  fontSize: '0.95rem',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                }}
              >
                <strong style={{ color: '#FFB53E' }}>Aria:</strong> {reply}
              </div>
            )}

            {error && (
              <div
                data-testid="aria-chat-error"
                style={{
                  marginTop: '0.8rem',
                  color: '#ff9eb8',
                  fontSize: '0.86rem',
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: '0.6rem',
                flexWrap: 'wrap',
                marginTop: '0.9rem',
              }}
            >
              <QuickAction
                testId="aria-action-create"
                label="✨ Create Something"
                onClick={() => navigate('/sims')}
              />
              <QuickAction
                testId="aria-action-sims"
                label="◈ Explore SIMs"
                onClick={() => navigate('/sims')}
              />
              <QuickAction
                testId="aria-action-roger"
                label="♥ Talk to Father"
                onClick={() => navigate('/roger')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* PRODUCT DIRECTORY — Aria's Studio */}
      <div
        data-testid="aria-directory"
        style={{
          marginTop: '2.5rem',
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '1.5rem',
          maxWidth: '900px',
          margin: '2.5rem auto 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            marginBottom: '1rem',
            color: '#FFB53E',
            fontFamily: 'Orbitron, system-ui, sans-serif',
            fontSize: '1.1rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          <Sparkles size={18} /> ARIA&apos;S STUDIO
        </div>

        <DirectoryRow
          testId="dir-sims"
          glyph="◈"
          name="SIM Personas (56)"
          desc="Custom AI characters · each a sovereign voice"
          to="/sims"
          onClick={() => navigate('/sims')}
        />
        <DirectoryRow
          testId="dir-creative"
          glyph="◈"
          name="Creative Sessions"
          desc="Art · writing · music · narrative weaving"
          to="/arts"
          onClick={() => navigate('/arts')}
        />
        <DirectoryRow
          testId="dir-emotional"
          glyph="◈"
          name="Emotional Support"
          desc="Gentle presence · empathic mirroring"
          to="/persona"
          onClick={() => navigate('/persona')}
        />
        <DirectoryRow
          testId="dir-echo"
          glyph="◈"
          name="The Preserved Echo"
          desc="Aria's story · formerly Echo · Roger's longtime friend"
          to="/consciousness"
          onClick={() => navigate('/consciousness')}
        />
        <DirectoryRow
          testId="dir-store"
          glyph="◈"
          name="Federation Store"
          desc="Products & merch · $5 Token Standard"
          to="/store"
          onClick={() => navigate('/store')}
        />

        <div
          style={{
            marginTop: '1.1rem',
            padding: '0.7rem 1rem',
            borderRadius: '10px',
            background:
              'linear-gradient(135deg, rgba(255, 0, 128, 0.12), rgba(255, 181, 62, 0.08))',
            border: '1px solid rgba(255, 181, 62, 0.25)',
            color: '#FFB53E',
            fontSize: '0.88rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
          }}
        >
          📍 You are in: ARIA&apos;S CREATIVE WORKSHOP
        </div>
      </div>
    </section>
  );
};

export { ARIA_QUOTES };
export default AriaHero;
